import {
  CodepenIcon,
  File06Icon,
  Image01Icon,
  Image02Icon,
} from '@gohighlevel/ghl-icons/24/outline'
import axios, {
  AxiosError,
  AxiosProgressEvent,
  CancelTokenSource,
  isAxiosError,
} from 'axios'

import { AttachmentService } from '@/service/AttachmentService'
import { useAppStore } from '@/store/app'
import {
  IDocumentAttachment,
  getTranslatedErrorMessage,
} from '@gohighlevel/ghl-proposals-common'
import { v4 as uuid } from 'uuid'
import { ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute } from 'vue-router'
import { useDocument } from './useDocument'
import { useNotify } from './useNotify'

const isUploading = ref(false)
const uploadPercentage = ref(0)
const errorMessage = ref<string | null>(null)
const attemptId = ref('')
const uploadCancelTokenSources: CancelTokenSource[] = []

// Add allowed file types constant
export const ALLOWED_FILE_TYPES = [
  'image/jpeg',
  'image/jpg',
  'image/png',
  'image/gif',
  'image/svg+xml',
  'application/pdf',
]

const MAX_TOTAL_SIZE = 15 * 1024 * 1024 // 15MB in bytes
const MAX_FILES = 10 // Maximum number of files allowed
// Add file type to icon mapping
export const FILE_TYPE_ICONS = {
  jpeg: Image01Icon,
  jpg: Image01Icon,
  png: Image01Icon,
  gif: Image02Icon,
  svg: CodepenIcon,
  pdf: File06Icon,
} as const

// Helper function to get icon for file type
export const getFileTypeIcon = (fileType: string) => {
  // Extract extension from MIME type if present
  const extension =
    fileType?.split('/')?.pop()?.toLowerCase() || fileType?.toLowerCase()
  return (
    FILE_TYPE_ICONS[extension as keyof typeof FILE_TYPE_ICONS] || File06Icon
  )
}

export const useAttachments = () => {
  const { t } = useI18n()
  const route = useRoute()
  const document = useDocument()
  const { handleSuccess } = useNotify()
  const appStore = useAppStore()
  const locationId = route.params.locationId as string

  const createCancelTokenSource = () => axios.CancelToken.source()

  const resetErrorMessage = async () => {
    errorMessage.value = null
  }

  const cancelUpload = async () => {
    if (uploadCancelTokenSources.length) {
      uploadCancelTokenSources.forEach(source => {
        source.cancel(t('validations.warnings.uploadCancelled'))
      })
      isUploading.value = false
    }
  }

  const validateFile = (file: File): { valid: boolean; error?: string } => {
    // Check file type
    if (!ALLOWED_FILE_TYPES.includes(file.type)) {
      return {
        valid: false,
        error: t('validations.warnings.invalidFileType'),
      }
    }

    // Check file size
    if (file.size > MAX_TOTAL_SIZE) {
      return {
        valid: false,
        error: t('validations.warnings.fileExceedsLimit'),
      }
    }

    return { valid: true }
  }

  const validateTotalFiles = (
    newFiles: File[] = []
  ): { valid: boolean; error?: string } => {
    const currentAttachments = appStore.document.emailAttachments || []
    const totalFiles = currentAttachments.length + newFiles.length

    if (totalFiles > MAX_FILES) {
      return {
        valid: false,
        error: t('validations.warnings.maxFilesExceeded', { max: MAX_FILES }),
      }
    }

    return { valid: true }
  }

  const validateTotalSize = (
    newFiles: File[]
  ): { valid: boolean; error?: string } => {
    const currentAttachments = appStore.document.emailAttachments || []
    const currentSize = currentAttachments.reduce(
      (acc, curr) => acc + curr.size,
      0
    )
    const newSize = newFiles.reduce((acc, curr) => acc + curr.size, 0)
    const totalSize = currentSize + newSize

    if (totalSize > MAX_TOTAL_SIZE) {
      return {
        valid: false,
        error: t('validations.warnings.totalSizeExceeded'),
      }
    }

    return { valid: true }
  }

  const uploadAttachment = async (
    file: File,
    documentId: string,
    onUploadProgress: (progressEvent: AxiosProgressEvent) => void
  ) => {
    try {
      attemptId.value = uuid()
      const cancelToken = createCancelTokenSource()
      uploadCancelTokenSources.push(cancelToken)
      resetErrorMessage()
      isUploading.value = true

      // Validate file type and size
      const fileValidation = validateFile(file)
      if (!fileValidation.valid) {
        errorMessage.value = fileValidation.error as string
        isUploading.value = false
        return
      }

      const { data } = await AttachmentService.uploadAttachment({
        locationId,
        file,
        documentId,
        attemptId: attemptId.value,
        onUploadProgress: onUploadProgress,
        cancelToken: cancelToken.token,
      })
      return data
    } catch (error) {
      isUploading.value = false
      if (axios.isCancel(error)) {
        console.error('Request canceled:', error.message)
      }
      if (isAxiosError(error)) {
        const axiosError = error as AxiosError
        if (axiosError?.response) {
          errorMessage.value = getTranslatedErrorMessage(axiosError, t)
        }
      }
    }
  }

  const uploadInBatches = async (
    list: { file: File; documentId: string }[],
    batchSize = 2
  ) => {
    // Validate total number of files
    const filesValidation = validateTotalFiles(list.map(item => item.file))
    if (!filesValidation.valid) {
      errorMessage.value = filesValidation.error as string
      return
    }

    // Validate total size
    const totalSizeValidation = validateTotalSize(list.map(item => item.file))
    if (!totalSizeValidation.valid) {
      errorMessage.value = totalSizeValidation.error as string
      return
    }

    const results: IDocumentAttachment[] = []
    let index = 0

    const processNext = async () => {
      if (index >= list.length) return

      const { file, documentId } = list[index]
      const currentIndex = index
      index++

      try {
        const response = await uploadAttachment(
          file,
          documentId,
          (progressEvent: AxiosProgressEvent) => {
            const { loaded, total = 0 } = progressEvent
            const percent = Math.round((loaded / total) * 100)
            uploadPercentage.value = percent
          }
        )
        if (response) {
          const { name, storagePath, size, type, publicUrl } = response
          results[currentIndex] = {
            name,
            storagePath,
            size,
            type,
            publicUrl,
          }
          await processNext()
        }
      } catch (error) {
        console.error(`Error uploading file ${currentIndex}:`, error)
      }
    }

    const initialBatch = []
    while (initialBatch.length < batchSize && index < list.length) {
      initialBatch.push(processNext())
    }

    await Promise.all(initialBatch)

    // Update document with new attachments
    if (results.length > 0) {
      const currentAttachments = appStore.document.emailAttachments || []
      const validResults = results.filter(Boolean)
      appStore.updateDocumentEmailAttachments([
        ...currentAttachments,
        ...validResults,
      ])
      await document.saveDocument()
      handleSuccess(t('common.attachmentsUploadedSuccessfully'))
    }

    isUploading.value = false
    uploadPercentage.value = 0
  }

  const removeAttachment = async (attachmentStoragePath: string) => {
    const currentAttachments = appStore.document.emailAttachments || []
    const updatedAttachments = currentAttachments.filter(
      attachment => attachment.storagePath !== attachmentStoragePath
    )
    appStore.updateDocumentEmailAttachments(updatedAttachments)
  }

  return {
    uploadAttachment,
    uploadInBatches,
    removeAttachment,
    isUploading,
    uploadPercentage,
    errorMessage,
    resetErrorMessage,
    cancelUpload,
    validateFile,
    validateTotalFiles,
    validateTotalSize,
    ALLOWED_FILE_TYPES,
    MAX_TOTAL_SIZE,
    MAX_FILES,
    getFileTypeIcon,
  }
}
